/**
 * NotFoundPage — the 404 (s216, SEO phase 1 item 5).
 *
 * ── WHY THIS PAGE EXISTS, AND WHAT IT DOES NOT FIX ──────────────────────────────────────
 * Measured s216 (matching the audit): every unknown path on the domain returned the HTML shell
 * with **200 OK**. A crawler cannot tell a real page from a typo, so it indexes junk paths.
 *
 * 🔴 THIS PAGE CARRIES `noindex` BUT THE HTTP STATUS IS STILL 200 — and that is a DECISION, not
 * an oversight. The ask said: return a real 404 status "if the Railway static config can return
 * a real 404 status for unknown paths without breaking client routing… if not, say so and leave
 * the noindex page". It cannot be done safely here, for a reason worth recording:
 *
 *   `serveStatic` (`server/vite.ts`) ends in `app.use("*")` → `sendFile(index.html)`. To answer
 *   404 the SERVER would have to know which paths are real CLIENT routes. The public routes are
 *   enumerable (`shared/constants/public-routes.ts`), but the AUTHENTICATED surface is not: the
 *   app's routes are declared inside four role dashboards (`ClientDashboard`, `AdvisorDashboard`,
 *   `ExecutorDashboard`, `AdministratorDashboard`), many with parameters. A hand-kept server-side
 *   list of them would be exactly the drift-prone hand-listed projection this codebase has been
 *   burned by before (D-167.6), and the failure mode is silent and bad: a real page starts
 *   answering 404 to monitors and crawlers while still rendering fine in a browser, so nobody
 *   notices.
 *
 * 🔴 The clean fix is PRE-RENDERING, which is the phase-2 question. Once each marketing route is
 * a real file on disk, `express.static` serves the real ones and the catch-all can answer a true
 * 404 for everything else with no list to maintain. Recorded in the phase-2 write-up as a
 * second reason to prefer the pre-render.
 *
 * `noindex` is the part that ships today, and it is the part that stops junk paths being indexed.
 */
import { useLocation } from 'wouter';
import { WARM, FONT, PRODUCT_NAME } from '@shared/brand';
import { PublicLayout, CONTAINER } from '@/components/public/PublicLayout';
import { Seo } from '@/components/public/Seo';

export default function NotFoundPage() {
  const [, navigate] = useLocation();

  return (
    <PublicLayout>
      <Seo
        path="/404"
        title={`Page not found — ${PRODUCT_NAME}`}
        description={`That page does not exist. Find your way back to ${PRODUCT_NAME}.`}
        noindex
      />
      <section style={{ background: WARM.cream, padding: '96px 32px 120px', minHeight: '60vh' }}>
        <div className={CONTAINER} style={{ maxWidth: 620, textAlign: 'center' }}>
          <div style={{ fontFamily: FONT.serif, fontSize: 64, fontWeight: 400, color: WARM.stone, lineHeight: 1, marginBottom: 20 }}>404</div>
          <h1 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 38, lineHeight: 1.15, letterSpacing: '-0.01em', color: WARM.ink, margin: '0 0 16px' }}>
            That page doesn&rsquo;t exist.
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: '#514D44', margin: '0 auto 32px', maxWidth: 460 }}>
            The link may be out of date, or the address may have a typo in it.
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* A real anchor: this is navigation, and it is the only link out of a dead end. */}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              style={{ fontSize: 16, color: WARM.charcoal, border: `1px solid ${WARM.stone}`, padding: '14px 26px', borderRadius: 8, textDecoration: 'none', fontFamily: 'inherit' }}
            >
              Back to home
            </a>
            {/* Sign in is offered here because an unmatched APP path used to redirect straight to
                /login. If APP_ROUTE_PREFIXES ever goes stale, this is the link that keeps a
                logged-out visitor from being stranded (see that constant's header). */}
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
              style={{ fontSize: 16, color: WARM.charcoal, border: `1px solid ${WARM.stone}`, padding: '14px 26px', borderRadius: 8, textDecoration: 'none', fontFamily: 'inherit' }}
            >
              Sign in
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
